import { faUpRightFromSquare } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { StatusCode } from "api/protocols";
import { GetLoyaltyCustomer } from "api/rpc/2024-04/facilityAdmin/customer/loyaltyCustomer";
import axios, { CancelToken } from "axios";
import { Badge } from "components/badge/Badge";
import Card from "components/card/Card";
import DataTable from "components/dataTable/DataTable";
import Search from "components/search/Search";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import React, { SetStateAction, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { showError } from "redux/actions/ui";
import { ILoyaltyCustomer, ITier } from "redux/reducers/models/loyalty";

const LIMIT = 50;

interface ILoyaltyCustomerExtended extends ILoyaltyCustomer {
    customer_first_name: string;
    customer_last_name: string;
    customer_email: string;
    customer_type: string;
    selected_tier?: ITier;
}

export default function LoyaltyCustomers() {
    const {t} = useTranslation();
    const dispatch = useAppDispatch();
    const [customers, setCustomers] = useState<Array<ILoyaltyCustomerExtended>>(undefined);
    const [filterState, setFilterState] = useState({offset: 0, search: ""});
    const {facilityStore} = useAppSelector(store => store); 

    useEffect(() => {
        const source = axios.CancelToken.source();
        void loadLoyaltyCustomers(source.token);
        return () => source.cancel();
    }, [filterState]);

    async function loadLoyaltyCustomers(cancelToken?: CancelToken) {
        //loader
        if (customers !== undefined) {
            setCustomers(undefined);
        }
        const res = await GetLoyaltyCustomer({
            limit: LIMIT, 
            offset: filterState.offset, 
            user: true, 
            search: filterState?.search ? filterState?.search : undefined, 
            tier: facilityStore?.loyaltyProgram?.type === "tier_based" ? true : undefined}, false, cancelToken);
        if (res?.status !== StatusCode.OK) {
            if (cancelToken && cancelToken?.reason) {
                return;
            }
            setCustomers([]);
            dispatch(showError(t("Error getting customers")));
            return;
        }
        setCustomers(res?.data as SetStateAction<ILoyaltyCustomerExtended[]>);
    }

    function navigateToProfile(customerId: number) {
        window.open(`/admin/customers/${customerId}/profile`);
    }

    return (
        <Card title={t("Customers")} titleDetails={[{content: <Search historyKey={""} searchCallback={search => setFilterState(prev => ({...prev, search}))}/>}]}> 
            <Card.Section table>
                <br />
            <DataTable
                    columns={[{ label: t("Customer Name") }, {label: "", width: "20%"}, {label: t("Points")}, ...(facilityStore?.loyaltyProgram?.type === "tier_based" ? [{label: t("Tier")}] : []), {label: t("Membership")}, {label: ""} ]}
                    loading={customers === undefined}
                    footer={{
                        tableLimit: LIMIT,
                        tableOffset: filterState.offset,
                        disableNextOffset: !customers || !(customers?.length === filterState.offset),
                        handleTableOffset: direction =>
                          setFilterState( prev => ({...prev, offset: direction === "prev" ? prev.offset - LIMIT : prev.offset + LIMIT })),
                      }}
                >
                    {customers?.map((customer, index) => {
                        return (
                            <tr key={index}>
                                <td><p className="loyalty-customers-name">{customer?.customer_first_name} {customer?.customer_last_name}</p> <p>{customer?.customer_email}</p></td>
                                <td></td>
                                <td>{customer?.points_balance}</td>
                                {facilityStore?.loyaltyProgram?.type === "tier_based" ? (<td>{customer?.selected_tier ? <Badge size="small" type="gray">{customer?.selected_tier?.title}</Badge> : "-"}</td>) : null}
                                <td>{customer?.customer_type}</td>
                                <td>{<FontAwesomeIcon className="cursor-pointer" icon={faUpRightFromSquare} onClick={() => navigateToProfile(customer?.customer_id)} />}</td>
                            </tr>
                        );
                    })}
                    </DataTable>
            </Card.Section>
        </Card>
    );
}